// Multievento, Resumen: hora de un servicio de la cabecera (o, si es un
// complemento, a qué servicio acompaña) que llega al mismo servicio de cada
// jornada, y debajo la hora de ese servicio en cada día ("Día 1 (17/11)
// 10:00 a 12:00 · Día 2 (18/11) 09:30 a 11:00 hora propia").
//
// Guarda con PUT /presupuestos/{id}/servicios/{linea}/horario: con alcance
// "sincronizadas" se respetan los días con hora propia (puesta a mano en la
// jornada y distinta); "Igualar todas" usa "todas". Las horas se guardan al
// salir del campo o tras 800 ms sin cambios, y luego se recarga la ficha
// (onActualizado). Los días facturados o celebrados no se tocan (aviso).
//
// Props: presupuesto, indice (posición en la cabecera), linea (la línea de la
// cabecera, la misma que presupuesto.menus_personalizados[indice]), fHorario
// (clasificación del servidor: estadoReglas.horarios.lineas[indice]),
// serviciosHorario, claseHorario, guia (pone data-guia="multievento-horas"),
// chip(texto, tono, extra), onActualizado, soloLectura (por defecto, sin el
// módulo de presupuestos: texto sin campos ni "Igualar todas", F1).
//
// Un servicio repetido en un día sale con todas sus horas ("Día 1 09:00 a
// 10:00 y 17:00 a 18:00", F4); un día cuyo servicio se sale del horario de la
// jornada lleva el aviso "Fuera del horario del día" (F6) y uno con inicio y
// sin fin, "sin hora de fin" (F3). Si el control se desmonta con una pareja
// válida sin guardar, se guarda (F7).
import React, { useEffect, useRef, useState } from 'react';
import { presupuestosAPI } from '../../api/api';
import { toast } from '../../hooks/use-toast';
import usePuedeEditarHorarios from '../../hooks/usePuedeEditarHorarios';
import useEsMovil from '../../hooks/useEsMovil';
import { horarioServicioPorJornada } from '../../utils/multieventoCabecera';
import { hoyMadrid, textoDetalleApi } from '../../utils/fechas';
import { CLASE_ERROR_FRANJA, errorFranja, fueraDeHorario, textoHorasServicio } from '../../utils/horasServicio';

const RETARDO_MS = 800;
const TEXTO_IGUALAR = 'Se pondrá la misma hora en todos los días, también en los que tienen hora propia o no tienen hora. ¿Seguir?';

const fechaCorta = (f) => (f && f.length >= 10 ? `${f.slice(8, 10)}/${f.slice(5, 7)}` : '');
const textoHoras = (ini, fin) => (ini || fin ? `${ini || '?'} a ${fin || '?'}` : 'sin hora');
// Móvil (< 768 px): campos de 44 px y texto de 16 px (iOS no hace zoom al tocarlos)
const CLASE_CAMPO_MOVIL = 'h-11 px-2 border border-[#CBD3CF] rounded-lg text-base text-[#33443C] bg-white tabular-nums focus:outline-none focus:ring-1 focus:ring-duetto-primary';

const esIdReal = (lid) => !!lid && !String(lid).startsWith('lv-');

// Referencia de la línea para el endpoint: su linea_id o su posición
export const refLineaCabecera = (linea, indice) => (esIdReal(linea?.linea_id) ? linea.linea_id : String(indice));

// Guarda hora, tipo o "Acompaña a" de una línea de la cabecera del multievento
// (y de ese servicio en cada jornada). Avisa de los días que no se tocaron.
// Devuelve la respuesta o null si falla (con su aviso).
// `antes`: lo pendiente de la ficha (autoguardado.guardarYa) se guarda antes,
// para que un guardado con la cabecera vieja no pise luego la hora nueva.
export const guardarHorarioServicio = async ({
  presupuestoId, indice, linea, cambios, alcance = 'sincronizadas', antes = null,
}) => {
  if (antes) {
    try {
      await antes();
    } catch (_e) {
      // El guardado pendiente avisa por su lado; la hora se guarda igual
    }
  }
  try {
    const { data } = await presupuestosAPI.horarioServicio(presupuestoId, refLineaCabecera(linea, indice), {
      ...cambios, menu_id: linea?.menu_id || '', alcance,
    });
    if (data?.aviso_omitidas) {
      toast({ title: 'Algunos días no se han cambiado', description: data.aviso_omitidas });
    }
    if (data?.aviso_fuera_de_horario) {
      toast({ title: 'Fuera del horario del día', description: data.aviso_fuera_de_horario });
    }
    return data;
  } catch (error) {
    toast({
      variant: 'destructive',
      title: 'No se ha guardado el horario',
      description: textoDetalleApi(error?.response?.data?.detail, error?.message || 'Error'),
    });
    return null;
  }
};

// "09:00 a 10:00 y 17:00 a 18:00" con todas las horas del día
const textoFranjasDia = (d) => (d.franjas && d.franjas.length > 1
  ? d.franjas.map(x => textoHoras(x.inicio, x.fin)).join(' y ')
  : textoHoras(d.inicio, d.fin));

// Aviso F6 del día (el primero que se salga de su horario)
const fueraDelDia = (d) => {
  for (const x of (d.franjas || [d])) {
    const t = fueraDeHorario(x.inicio, x.fin, d.refInicio, d.refFin, 'día');
    if (t) return t;
  }
  return null;
};

const HorarioServicioMultievento = ({
  presupuesto, indice, linea, fHorario, serviciosHorario, claseHorario, guia, chip, onActualizado,
  onAntesDeGuardar = null, soloLectura = null,
}) => {
  const puedeEditar = usePuedeEditarHorarios();
  const esMovil = useEsMovil();
  const claseCampo = esMovil ? CLASE_CAMPO_MOVIL : claseHorario;
  const lectura = soloLectura ?? !puedeEditar;
  // Horas mientras se escriben ({ ini, fin }); null = las guardadas
  const [borrador, setBorrador] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const borradorRef = useRef(null);
  const timerRef = useRef(null);
  const colaRef = useRef(Promise.resolve());
  const montadoRef = useRef(true);
  // Borrador que se está enviando: al salir del campo justo después del
  // retardo no se vuelve a enviar
  const enviandoRef = useRef(null);
  const lineaRef = useRef(linea);
  lineaRef.current = linea;

  // Al desmontar (otra pestaña, otra jornada) se guarda lo escrito si la
  // pareja vale, como las horas de la jornada (F7)
  useEffect(() => {
    montadoRef.current = true;
    return () => {
      montadoRef.current = false;
      clearTimeout(timerRef.current);
      if (borradorRef.current) guardarRef.current();
    };
  }, []);

  // Las peticiones van de una en una, en el orden en que se hacen
  const enviar = (cambios, alcance = 'sincronizadas') => {
    const tarea = colaRef.current.then(async () => {
      if (montadoRef.current) setGuardando(true);
      const data = await guardarHorarioServicio({
        presupuestoId: presupuesto?.id, indice, linea: lineaRef.current, cambios, alcance, antes: onAntesDeGuardar,
      });
      if (data && onActualizado) await onActualizado();
      if (montadoRef.current) setGuardando(false);
      return data;
    });
    colaRef.current = tarea.catch(() => null);
    return tarea;
  };

  const guardarBorrador = () => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
    const b = borradorRef.current;
    if (!b) return;
    const actual = lineaRef.current || {};
    if (b.ini === (actual.hora_inicio_servicio || '') && b.fin === (actual.hora_fin_servicio || '')) {
      borradorRef.current = null;
      if (montadoRef.current) setBorrador(null);
      return;
    }
    // Fin antes que inicio: no se guarda; el borrador se queda en rojo
    if (errorFranja(b.ini, b.fin)) return;
    if (enviandoRef.current === b) return;
    enviandoRef.current = b;
    enviar({ hora_inicio: b.ini, hora_fin: b.fin }).finally(() => {
      if (enviandoRef.current === b) enviandoRef.current = null;
      // Si se ha seguido escribiendo mientras se guardaba, el borrador nuevo se queda
      if (montadoRef.current && borradorRef.current === b) {
        borradorRef.current = null;
        setBorrador(null);
      }
    });
  };
  const guardarRef = useRef(guardarBorrador);
  guardarRef.current = guardarBorrador;

  const cambiarHora = (campo, valor) => {
    const base = borradorRef.current
      || { ini: linea?.hora_inicio_servicio || '', fin: linea?.hora_fin_servicio || '' };
    const b = { ...base, [campo]: valor };
    borradorRef.current = b;
    setBorrador(b);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => guardarRef.current(), RETARDO_MS);
  };

  const esComplemento = fHorario?.clase === 'complemento';
  const ini = borrador ? borrador.ini : (linea?.hora_inicio_servicio || '');
  const fin = borrador ? borrador.fin : (linea?.hora_fin_servicio || '');
  const dias = esComplemento ? [] : horarioServicioPorJornada(linea, presupuesto, hoyMadrid());
  // Un servicio repetido en el día conserva sus horas propias con "Igualar
  // todas" (el servidor no las junta): no cuenta para el botón
  const conPropia = dias.filter(d => d.propia && !d.bloqueo && !d.repetido);
  // Días sin hora aunque la cabecera la tenga: la orden de cocina nueva y la
  // hoja de material de ese día salen sin ella; "Igualar todas" la pone
  const conSinHora = dias.filter(d => d.sinHora && !d.bloqueo);
  const cabTieneHora = !!(linea?.hora_inicio_servicio || linea?.hora_fin_servicio);
  const esServicio = !esComplemento && (fHorario ? fHorario.clase === 'servicio' : true);
  const sinFin = esServicio && !!ini && !fin && !errorFranja(ini, fin);

  const igualarTodas = () => {
    if (!window.confirm(TEXTO_IGUALAR)) return;
    enviar({ hora_inicio: linea?.hora_inicio_servicio || '', hora_fin: linea?.hora_fin_servicio || '' }, 'todas');
  };

  const listaDias = dias.length > 0 && (
    <div className="basis-full flex flex-wrap items-center gap-x-1.5 gap-y-0.5" data-testid={`r2-horario-dias-${indice}`}>
      {dias.map((d, i) => {
        const fuera = d.bloqueo ? null : fueraDelDia(d);
        const sinFinDia = !d.bloqueo && (d.franjas || [d]).some(x => x.inicio && !x.fin);
        return (
          <span key={d.jornadaId || `dia-${d.numero}`} className="inline-flex items-center gap-1">
            {i > 0 && <span aria-hidden="true" className="text-[#B8C2BD]">·</span>}
            <span>Día {d.numero}{d.fecha ? ` (${fechaCorta(d.fecha)})` : ''}</span>
            <span className={`tabular-nums font-semibold ${d.propia ? 'text-[#92400E]' : 'text-[#33443C]'}`}
              data-testid={`r2-horario-dia-horas-${indice}-${d.numero}`}>
              {textoFranjasDia(d)}
            </span>
            {d.propia && chip('hora propia', 'ambar')}
            {d.sinHora && !d.bloqueo && chip(lectura ? 'sin hora' : 'sin hora: Igualar todas la pone', 'ambar',
              { 'data-testid': `r2-horario-dia-sin-hora-${indice}-${d.numero}` })}
            {sinFinDia && !d.sinHora && chip('sin hora de fin', 'ambar',
              { 'data-testid': `r2-horario-dia-sin-fin-${indice}-${d.numero}` })}
            {fuera && chip(fuera, 'ambar', { 'data-testid': `r2-horario-dia-fuera-${indice}-${d.numero}` })}
            {d.bloqueo && chip(d.bloqueo, 'gris')}
          </span>
        );
      })}
      {!lectura && (conPropia.length > 0 || conSinHora.length > 0) && cabTieneHora && (
        <button type="button" onClick={igualarTodas} disabled={guardando}
          className={`ml-1 font-semibold text-[#11704F] hover:underline disabled:opacity-50${esMovil ? ' min-h-[40px] px-2' : ''}`}
          title="Poner la hora de arriba en todos los días, también en los de hora propia"
          data-testid={`r2-igualar-horas-${indice}`}>
          Igualar todas
        </button>
      )}
    </div>
  );

  if (lectura) {
    // Sin clasificación (el estado de las reglas no llega) y sin ninguna
    // hora: puede ser un complemento; no se enseña nada
    if (!fHorario && !ini && !fin && dias.every(d => !d.inicio && !d.fin)) return null;
    const acompana = (serviciosHorario || []).find(x => x.indice === fHorario?.acompana_a_indice);
    return (
      <>
        {!esComplemento && (
          <span className="inline-flex items-center gap-1" data-testid={`r2-horas-texto-${indice}`}>
            <span className="font-semibold">Hora</span>
            <span className="tabular-nums font-semibold text-[#33443C]">{textoHorasServicio(ini, fin)}</span>
            <span className="text-[#8A9892]">todos los días</span>
          </span>
        )}
        {esComplemento && chip(
          acompana || fHorario?.acompana_a_nombre
            ? `Acompaña a ${acompana?.nombre || fHorario.acompana_a_nombre}${acompana?.hora_inicio ? ` (${acompana.hora_inicio})` : ''}`
            : 'Complemento', 'gris', { 'data-testid': `r2-acompana-texto-${indice}` })}
        {listaDias}
      </>
    );
  }

  return (
    <>
      {!esComplemento && (
        <span className={esMovil ? 'basis-full w-full flex flex-wrap items-center gap-2' : 'inline-flex items-center gap-1'}
          data-guia={guia ? 'multievento-horas' : undefined}>
          <span className="font-semibold">Hora</span>
          <input
            type="time"
            value={ini}
            onChange={(e) => cambiarHora('ini', e.target.value)}
            onBlur={() => guardarRef.current()}
            className={esMovil ? `${claseCampo} flex-1 min-w-[6.5rem]` : `${claseHorario} w-[5.5rem]`}
            title="Hora de inicio del servicio en todos los días"
            aria-label="Hora de inicio del servicio en todos los días"
            data-uso="presupuesto.multievento_horas"
            data-testid={`r2-hora-inicio-${indice}`}
          />
          <span>a</span>
          <input
            type="time"
            value={fin}
            onChange={(e) => cambiarHora('fin', e.target.value)}
            onBlur={() => guardarRef.current()}
            className={`${esMovil ? `${claseCampo} flex-1 min-w-[6.5rem]` : `${claseHorario} w-[5.5rem]`} ${sinFin ? 'border-[#F4D9AE] bg-[#FDF0DC]' : ''}`}
            title="Hora de fin del servicio en todos los días"
            aria-label="Hora de fin del servicio en todos los días"
            data-uso="presupuesto.multievento_horas"
            data-testid={`r2-hora-fin-${indice}`}
          />
          <span className={`text-[#8A9892]${esMovil ? ' basis-full' : ''}`}>todos los días</span>
          {errorFranja(ini, fin) && (
            <span className={CLASE_ERROR_FRANJA} data-testid={`r2-hora-error-${indice}`}>
              {errorFranja(ini, fin)}{borrador ? ': no se guarda' : ''}
            </span>
          )}
        </span>
      )}
      {!esComplemento && fHorario?.clase === 'servicio' && fHorario.sin_hora && chip(
        `Sin hora${fHorario.propuesta_inicio ? ` (propuesta ${fHorario.propuesta_inicio} - ${fHorario.propuesta_fin || '?'})` : ''}`,
        'ambar', { 'data-testid': `r2-sin-hora-${indice}` })}
      {sinFin && chip('Sin hora de fin', 'ambar', { 'data-testid': `r2-sin-fin-${indice}` })}
      {esComplemento && (serviciosHorario.length > 0 ? (
        <label className={esMovil ? 'basis-full w-full flex flex-col gap-1 min-w-0' : 'inline-flex items-center gap-1 min-w-0'}>
          <span className="font-semibold whitespace-nowrap">Acompaña a</span>
          <select
            value={fHorario.acompana_a_indice ?? ''}
            disabled={guardando}
            onChange={(e) => {
              const destino = parseInt(e.target.value, 10);
              if (Number.isNaN(destino)) return;
              const lid = presupuesto?.menus_personalizados?.[destino]?.linea_id;
              enviar(esIdReal(lid)
                ? { acompana_a_indice: destino, acompana_a_linea_id: lid }
                : { acompana_a_indice: destino });
            }}
            className={esMovil ? `${claseCampo} w-full min-w-0` : `${claseHorario} min-w-0 max-w-[16rem]`}
            title="Servicio al que acompaña este complemento (en todos los días)"
            data-uso="presupuesto.linea_acompana"
            data-testid={`r2-acompana-${indice}`}
          >
            {fHorario.acompana_a_indice == null && <option value="" disabled>Elegir servicio</option>}
            {serviciosHorario.map(sv => (
              <option key={sv.indice} value={sv.indice}>
                {sv.nombre}{sv.hora_inicio ? ` (${sv.hora_inicio})` : ''}
              </option>
            ))}
          </select>
        </label>
      ) : chip('Complemento', 'gris'))}
      {guardando && <span className="text-[#8A9892]" data-testid={`r2-horario-guardando-${indice}`}>Guardando...</span>}
      {listaDias}
    </>
  );
};

export default HorarioServicioMultievento;
